import { MatDialog } from '@angular/material/dialog';
import { PosDetailWithOrders } from '../../../../../../core/models/pos.model';
import { OrdersService } from '../../../../ecommerce/services/orders.service';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { OrderSelectorComponent } from '../../../../ecommerce/pos/order-selector/order-selector.component';
import { PosService } from '../../../../ecommerce/services/pos.service';
import { Product } from 'app/core/models/product.model';
import { PosAccount } from '../../../../../../core/models/account.model';
import { RegisterService } from '../../../../ecommerce/services/register.service';
import { OpenRegisterComponent } from '../../../../ecommerce/pos/open-register/open-register.component';
import { Router } from '@angular/router';
import { AccountsManagerComponent } from '../../../../ecommerce/pos/accounts-manager/accounts-manager.component';
import { RegisterStatus } from '../../../../../../core/models/register.model';
import { OrderItem } from '../../../../../../core/models/order-item.model';
import { Order } from '../../../../../../core/models/order.model';
import { FuseConfirmationService } from '../../../../../../../@fuse/services/confirmation';
import { OrderItemsService } from '../../../../ecommerce/services/order-items.service';
import { OpenTableData } from '../../../../../../core/integrations/nosERP/models/pos.model';
import { ShowQrCodeComponent } from '../../../../ecommerce/pos/show-qr-code/show-qr-code.component';
import { PrintService, PrintUserContext } from '../../../../../../core/services/print.service';
import { SettingsPrintersComponent } from '../../../settings/printers/printers.component';
import { WebSerialPrinterService } from '../../../../../../core/services/webserial-printer.service';

interface OrderByClientUI {
  id: string;
  name: string;
  items: OrderItemUI[];
  total: number;
}

export interface OrderItemUI extends OrderItem {
  total: number;
}

export interface OrderItemQuantityChangedEvent {
  id: string;
  quantity: number;
}

@Component({
  selector: 'order-list',
  templateUrl: './order-list.component.html',
  styleUrls: ['./order-list.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class OrderListComponent implements OnInit {
  @Input() pos: PosDetailWithOrders;
  @Input() orders: Order[];
  @Input() isMobileResolution: boolean;
  @Input() isViewOnly: boolean;
  @Input() loading!: boolean;
  @Output() productEditClicked: EventEmitter<OrderItemUI> = new EventEmitter<OrderItemUI>();
  @Output() productAdded = new EventEmitter<Product>();
  @Output() closeButtonClicked = new EventEmitter<void>();
  @Output() orderTotalChanged = new EventEmitter<number>();
  @Output() orderChanged = new EventEmitter<Order[]>();
  @Output() payClicked = new EventEmitter<void>();

  public ordersAndItemsWithTotal: OrderByClientUI[];
  public orderTotal: number;
  public showEmpty: boolean;
  isRestaurant: boolean;
  isAdmin: boolean;

  private postChangesSubject = new Subject<OrderItemQuantityChangedEvent>();
  isDragging: boolean;
  private accounts: PosAccount[];
  private accountsSubject = new Subject<PosAccount[]>();
  canShowCenas: boolean = false;
  selectedPrinterLabel: string;

  constructor(
    private orderService: OrdersService,
    private toastr: ToastrService,
    private translocoService: TranslocoService,
    private _matDialog: MatDialog,
    private posService: PosService,
    private registerService: RegisterService,
    private router: Router,
    private _fuseConfirmationService: FuseConfirmationService,
    private orderItemService: OrderItemsService,
    private printService: PrintService,
    private wsPrinter: WebSerialPrinterService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ('orders' in changes && changes.orders.currentValue !== undefined) {
      this.adaptOrder();
    }
  }

  ngOnInit(): void {
    this.loading = true;
    this.postChangesSubject.pipe(debounceTime(500)).subscribe(({ id, quantity }) => {
      this.executePostChanges(id, quantity);
    });

    this.isAdmin = JSON.parse(localStorage.getItem('user')).gerente === 1;
    this.isRestaurant = localStorage.getItem('isRestaurant') === 'true';
    this.updateSelectedPrinterLabel();
  }

  private async updateSelectedPrinterLabel(): Promise<void> {
    const dev = this.wsPrinter.lastDevice;
    const allPrinters = await this.wsPrinter.getGrantedPorts();
    if (allPrinters.length && dev && (dev.vendorId != null || dev.productId != null)) {
      try {
        this.selectedPrinterLabel = this.wsPrinter.identifyPrinter(dev);
      } catch {
        this.selectedPrinterLabel = 'Impressora selecionada';
      }
    } else {
      this.selectedPrinterLabel = 'Sem impressora';
    }
  }

  addQuantityToItem(orderItem: OrderItem): void {
    this.handleOrderItemQuantityChanged({
      id: orderItem.id,
      quantity: orderItem.quantity + 1
    });
  }

  removeQuantityFromItem(orderItem: OrderItem): void {
    if (orderItem.quantity === 0) {
      return;
    }
    this.handleOrderItemQuantityChanged({
      id: orderItem.id,
      quantity: orderItem.quantity - 1
    });
  }

  private adaptOrder(): void {
    if (!this.orders) {
      this.canShowCenas = true;
    }
    if (!this.orders) return;

    this.ordersAndItemsWithTotal = this.orders.map(order => {
      return {
        id: order.id,
        name: order.name,
        items: order.items?.map(orderItem => {
          return {
            ...orderItem,
            total: orderItem.product.price.EUR * orderItem.quantity
          };
        }),
        total: order.items?.reduce((acc, item) => {
          return acc + item.product.price.EUR * item.quantity;
        }, 0)
      };
    });

    this.canShowCenas = this.ordersAndItemsWithTotal.length === 0;

    this.orderTotal = this.ordersAndItemsWithTotal.reduce((acc, item) => acc + item.total, 0);
    this.orderTotalChanged.emit(this.orderTotal);
  }

  private handleOrderItemQuantityChanged(data: OrderItemQuantityChangedEvent): void {
    const { id, quantity } = data;

    // Step 1: Verify and Update Orders
    if (!this.orders) {
      console.error('Orders not found in posDetailWithOrders');
      return;
    }

    // Step 2: Map and Update the Relevant Order
    const updatedOrders = this.orders.map(order => {
      // Update items immutably
      const updatedItems = order.items?.map(item =>
        item.id === id
          ? { ...item, quantity } // Update quantity
          : item
      );

      return {
        ...order,
        items: updatedItems // Assign new items reference
      };
    });

    // Step 3: Replace reference to trigger Angular's change detection
    this.orders = updatedOrders; // Replace with new orders reference

    // Step 4: Adapt the UI
    this.adaptOrder(); // Update orderListTransformed and UI dependencies

    // Step 5: Emit Changes (this step depends on your app’s architecture)
    this.postChangesSubject.next({ id, quantity });
  }

  private async executePostChanges(id: string, quant: number): Promise<void> {
    let orderByClient = this.orders;

    if (quant === 0) {
      let confirmed = await this.promptDeleteItem(id);
      if (!confirmed) {
        this.handleOrderItemQuantityChanged({ id, quantity: 1 });
      }
    } else {
      await this.orderItemService.changeQuantity(id, quant).toPromise();
      this.adaptOrder();
    }
    if (!orderByClient) {
      return;
    }
  }

  async transferProductFromOrder(order: OrderByClientUI, rItem: OrderItemUI): Promise<void> {
    // Step 1: Validate input and check existing data
    if (!this.orders) {
      console.error('Orders not found in posDetailWithOrders');
      return;
    }

    try {
      // Step 2: Find the source order (containing the item to transfer)
      let sourceOrder = this.orders.find(o => o.items.some(item => item.product.id === rItem.product.id));

      if (!sourceOrder) {
        console.error('Source order not found for the item:', rItem);
        return;
      }

      // Step 3: Find the target order (target of the transfer)
      let targetOrder = this.orders.find(o => o.name === order.name);

      if (!targetOrder) {
        console.error('Target order not found:', order);
        return;
      }

      // Step 4: Check if the item already exists in the target order
      const existingItemInTargetOrder = targetOrder.items.find(item => item.product.id === rItem.product.id);

      if (existingItemInTargetOrder) {
        // Case 1: The item exists in the target order — update its quantity
        const newQuantity = existingItemInTargetOrder.quantity + rItem.quantity;

        // Call edit logic to update the quantity in the target order
        await this.orderItemService.changeQuantity(existingItemInTargetOrder.id, newQuantity).toPromise();
        this.handleOrderItemQuantityChanged({
          id: existingItemInTargetOrder.id,
          quantity: newQuantity
        });
      } else {
        const user = JSON.parse(localStorage.getItem('user'));

        // Case 2: The item doesn’t exist in the target order — add a new item
        const newItemPayload = {
          id_mesa: Number(this.pos.pos.id),
          mesa_conta_id: targetOrder.id, // Assuming targetOrder.id exists and represents mesa_conta_id
          quant: rItem.quantity,
          id_produto: Number(rItem.product.id),
          productId: rItem.product.id, // Keeping it in case the backend requires both
          quantity: rItem.quantity, // Keeping it in case the backend requires both
          orderName: targetOrder.name,
          nome: rItem.product.name,
          preco: rItem.product.price.EUR || 0, // Provide default price if undefined
          observacao: '', // Assuming no observations are provided
          adicional: '', // Assuming no additional options are provided
          id_utilizador: user.id_utilizador // Replace with actual user ID logic
        };

        // Call add logic to add the item to the target order
        await this.orderService.addOrderItemToOrder(order.id, newItemPayload).toPromise();
        const updatedOrders = this.orders.map(order => {
          if (order.id === sourceOrder.id) {
            return {
              ...order,
              items: sourceOrder.items.filter(item => item.product.id !== rItem.product.id)
            };
          }

          if (order.id === targetOrder.id) {
            return {
              ...order,
              items: [...order.items, { ...rItem, quantity: rItem.quantity }]
            };
          } else {
            return order;
          }
        });

        this.orderChanged.emit(updatedOrders);
      }

      // Step 5: Remove the item from the source order
      await this.doDeleteItem(rItem.id, true);

      // Step 6: Trigger UI updates
      this.adaptOrder();

      // Show success feedback
      this.toastr.success(
        this.translocoService.translate('Ecommerce.POS.transfer_success'),
        this.translocoService.translate('toast_messages.success')
      );
    } catch (error) {
      // Handle errors and show feedback
      console.error('Error during product transfer:', error);
      this.toastr.error(
        this.translocoService.translate('error_messages.transfer_failed'),
        this.translocoService.translate('error_messages.error_title')
      );
    }
  }

  onDropProduct($event: any, order: OrderByClientUI) {
    // If dragging between order item lists (not from product card), treat as transfer
    if (
      $event.previousContainer !== $event.container &&
      $event.previousContainer.element.nativeElement.tagName !== 'APP-PRODUCT-CART-CARD'
    ) {
      this.transferProductFromOrder(order, $event.item.data.item);
      return;
    }

    // Extract product data from drag event supporting both new and legacy shapes
    const data = $event.item?.data || {};
    const productId = data.id || data.id_produto;
    const productName = data.name || data.nome;
    const productPrice = (data.price && data.price.EUR) != null ? data.price.EUR : data.preco;

    // If no product id could be determined, fallback to previous behavior
    if (!productId) {
      this.productAdded.emit($event.item.data);
      return;
    }

    // Check whether the product already exists in the target order
    const existingItem = order.items?.find(item => item.product.id === productId);

    if (existingItem) {
      // Increase quantity of existing line
      this.addQuantityToItem(existingItem);
      return;
    }

    // Add as a new line directly to the specified order (avoid opening the account selector modal)
    const user = JSON.parse(localStorage.getItem('user'));
    this.posService
      .addRequest({
        id_mesa: this.pos.pos.id,
        id_produto: productId,
        preco: productPrice,
        mesa_conta_id: order.id.toString(),
        quant: 1,
        nome: productName,
        observacao: null,
        adicional: null,
        id_utilizador: user?.id_utilizador
      })
      .subscribe();
  }

  openEditProduct(item: OrderItemUI, isTransfer: boolean = false, changedItem?: OrderItemUI) {
    const dialogRef = this._matDialog.open(OrderSelectorComponent, {
      data: {
        pos: this.pos,
        tableId: this.pos.pos.id,
        mode: isTransfer ? 'transfer' : 'edit',
        quantity: isTransfer ? changedItem.quantity : item.quantity
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result?.quantity) {
        if (isTransfer) {
          this.handleOrderItemQuantityChanged({
            id: item.product.id,
            quantity: item.quantity + result?.quantity
          });
          const remainingQuantity = Math.max(changedItem.quantity - result?.quantity, 0);
          if (remainingQuantity === 0) {
            this.doDeleteItem(changedItem.product.id);
          } else {
            this.handleOrderItemQuantityChanged({ id: changedItem.product.id, quantity: remainingQuantity });
          }
        } else {
          this.handleOrderItemQuantityChanged({ id: item.id, quantity: result?.quantity });
        }
      }
    });
  }

  async deleteProduct(data: { item: OrderItemUI; deletable: boolean }) {
    this.promptDeleteItem(data.item.id);
  }

  // Predicate: Allow only items with allowedZone = 'zone1'
  canEnterDeleteZone = (drag: any): boolean => {
    return drag.data.deletable;
  };

  async promptDeleteItem(id: string) {
    const dialogRef = this._fuseConfirmationService.open({
      title: this.translocoService.translate('confirm_deletion.title'),
      message: this.translocoService.translate('confirm_deletion.message'),
      actions: {
        confirm: {
          label: this.translocoService.translate('confirm_deletion.confirm_button')
        },
        cancel: {
          label: this.translocoService.translate('confirm_deletion.cancel_button')
        }
      }
    });

    const result = await dialogRef.afterClosed().toPromise();

    if (result !== 'confirmed') return;

    await this.doDeleteItem(id);

    return result;
  }

  private async doDeleteItem(id: string, hideMessage?: boolean): Promise<void> {
    try {
      // Step 1: Verify that `posDetailWithOrders` and its `orders` exist
      if (!this.orders) {
        console.error('Orders not found in posDetailWithOrders');
        return;
      }

      // Step 2: Update orders immutably by removing the item with the given ID
      const updatedOrders = this.orders
        .map(order => {
          // Filter out the target item from the `items` array
          const updatedItems = order.items?.filter(item => item.id !== id);

          return {
            ...order,
            items: updatedItems // Assign a new array reference
          };
        })
        .filter(order => order.items?.length > 0); // Remove orders with no items

      // // Step 3: Replace the `orders` reference to trigger Angular’s change detection
      // this.posDetailWithOrders = {
      //   ...this.posDetailWithOrders, // Keep other fields intact
      //   orders: updatedOrders // Update orders with the new reference
      // };
      this.orders = updatedOrders;

      // Step 4: Call `adaptOrder` to update UI transformations like `orderListTransformed`
      this.adaptOrder();

      // Step 5: Invoke the service to delete the item from the backend
      await this.orderItemService.deleteOrderItem(id).toPromise();

      if (hideMessage) return;

      // Step 6: Show success feedback
      this.toastr.success(
        this.translocoService.translate('toast_messages.item_deleted_successfully'),
        this.translocoService.translate('toast_messages.success')
      );
    } catch (error) {
      // Step 7: Handle errors and show feedback
      console.error('Error during item deletion:', error);
      this.toastr.error(
        this.translocoService.translate('error_messages.deletion_failed'),
        this.translocoService.translate('error_messages.error_title')
      );
    }
  }

  orderHasItems(): boolean {
    return this.orders.some(order => order.items?.length > 0);
  }

  onDropProductAdd($event) {
    const user = JSON.parse(localStorage.getItem('user'));
    const params: OpenTableData = {
      acounts: [{ nome: 'default' }],
      geral: true,
      id_mesa: this.pos.pos.id,
      id_utilizador: user.id_utilizador,
      id_funcionario: user.id_funcionario,
      token: 0
    };

    if (this.pos.pos.status === 'disponivel') {
      this.posService.openTable(params).subscribe(() => {
        this.orderService
          .addOrderItemToOrder(this.pos.pos.id, {
            id_mesa: Number(this.pos.pos.id),
            id_produto: $event.item.data.id_produto,
            preco: $event.item.data.preco,
            mesa_conta_id: $event.item.data.orderId,
            quant: $event.item.data.quantity,
            nome: $event.item.data.nome,
            observacao: null,
            adicional: null,
            id_utilizador: user.id_utilizador
          })
          .subscribe();
      });
    } else {
      this.productAdded.emit($event.item.data);
    }
  }

  closeModal() {
    this.closeButtonClicked.emit();
  }

  startPayment() {
    this.payClicked.emit();
    this.registerService.closingStatus$.subscribe(res => {
      if (res.status === RegisterStatus.NONE_OPEN) {
        const dialogRef = this._matDialog.open(OpenRegisterComponent, {});
        dialogRef.afterClosed().subscribe((result: string) => {
          if (result) {
            this.router.navigate([`./pay/` + this.pos.pos.id], {
              queryParams: {
                posId: this.pos.pos.id
              }
            });
          }
        });
      } else {
        this.router.navigate(['ecommerce/pay/' + this.pos.pos.id]);
      }
    });
  }

  openAccountsManager() {
    const dialogRef = this._matDialog.open(AccountsManagerComponent, {
      width: '100vw',
      data: {
        pos: this.pos
      }
    });

    setTimeout(() => {
      this.accountsSubject.next(this.accounts);
    });
  }

  showQrCode() {
    const dialogRef = this._matDialog.open(ShowQrCodeComponent, {
      width: '500px',
      data: {
        pos: this.pos
      }
    });
  }

  printReceipt() {
    const user = JSON.parse(localStorage.getItem('user')) as PrintUserContext | null;
    this.printService.printControlReceipt(this.pos, user, this.orderTotal);
  }

  openPrinterSelector(): void {
    const dialogRef = this._matDialog.open(SettingsPrintersComponent, {
      width: this.isMobileResolution ? '100vw' : '720px',
      height: '100vh',
      panelClass: 'receiptOnModal',
      data: { selectionMode: true }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && (result.vendorId != null || result.productId != null)) {
        try {
          this.wsPrinter.setDefaultPrinter({ vendorId: result.vendorId, productId: result.productId });
          this.printReceipt();
        } catch (e) {
          // ignore
        }
      }
    });
  }
}
